import React, { useState } from 'react';
import moment from 'moment-timezone';
import { Button, Dropdown, Modal } from 'antd';
import { BellFilled, BellOutlined, DownOutlined } from '@ant-design/icons';
import {
	getWorkOrderScheduledAt,
	formatReminderTime,
	formatUpcomingReminder,
	getReminderStatus,
	getWorkOrderStarState,
	isWorkOrderRemindersEnabled,
	REMINDER_STATUS,
} from '../../data/workOrderStarLabels';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import useReminderNow from '../../hooks/useReminderNow';
import useReminderRinging from '../../hooks/useReminderRinging';
import connectStarActions, { StarActionProps } from './connectStarActions';
import useStarActions, { StarSnapshot } from './useStarActions';
import StarNotePopover from './StarNotePopover';
import useReminderSnoozeControls, { SnoozeIcon } from './useReminderSnoozeControls';
import OWButton from '../ow_button/OWButton';

interface OwnProps {
	workOrder: any;
}

type Props = OwnProps & StarActionProps;

// Upcoming: "Reminder in 2h 35m ▾", which opens the Track popover to change it. Cloned by
// StarNotePopover, which hands it onDropdownClick.
const ReminderCountdownPill: React.FC<{
	remindAt: moment.Moment;
	now: moment.Moment;
	onDropdownClick?: (event: React.MouseEvent) => void;
}> = ({ remindAt, now, onDropdownClick }) => (
	<span
		role="button"
		tabIndex={0}
		onClick={onDropdownClick}
		title={formatReminderTime(remindAt, now)}
		className="bodyregular inline-flex cursor-pointer items-center gap-xs rounded-sm bg-white px-s py-[6px] text-character-primary"
	>
		<BellOutlined />
		{formatUpcomingReminder(remindAt, now)}
		<DownOutlined className="text-xs text-character-secondary" />
	</span>
);

/**
 * The current user's reminder on a work order detail page, shown above the header actions.
 * Once the reminder rings — due on arrival, or fired while the user sits on the page — a modal
 * asks the user to snooze or stop it.
 */
const WorkOrderDetailReminder: React.FC<Props> = (props) => {
	const { workOrder } = props;
	const { starred, note, remindAt, condition } = getWorkOrderStarState(workOrder);
	const now = useReminderNow(starred ? remindAt : null);
	const workOrderId = workOrder ? workOrder.id : null;
	const isRinging = useReminderRinging(workOrderId, starred ? remindAt : null);
	const status = starred ? getReminderStatus(remindAt, isRinging) : REMINDER_STATUS.NONE;
	const { isPersisting, snooze, stop } = useStarActions(workOrderId, props);
	// Snoozing sets a new reminder time, so it's Pro-only like adding one; Stop is always offered.
	const canSnooze = props.canAddNote;

	// The due modal shows once per due reminder; closing it leaves the ringing bar.
	const reminderKey = remindAt ? `${workOrderId}-${remindAt.valueOf()}` : null;
	const [dismissedReminderKey, setDismissedReminderKey] = useState<string | null>(null);
	const dismissModal = () => setDismissedReminderKey(reminderKey);
	const current: StarSnapshot = { note, remindAt, condition };
	const { snoozeMenu, customSnoozeModal } = useReminderSnoozeControls({
		isPersisting,
		snoozeUntil: (until) => snooze(current, until),
		onPick: dismissModal,
	});

	if (!workOrderId || !isWorkOrderRemindersEnabled() || status === REMINDER_STATUS.NONE) {
		return null;
	}

	if (status === REMINDER_STATUS.UPCOMING) {
		return (
			<StarNotePopover
				workOrderId={workOrderId}
				initialStarred={starred}
				initialNote={note || ''}
				initialRemindAt={remindAt}
				initialCondition={condition}
				dueDate={nullSafeGet('dueDate', workOrder)}
				scheduledAt={getWorkOrderScheduledAt(workOrder)}
			>
				<ReminderCountdownPill remindAt={remindAt} now={now} />
			</StarNotePopover>
		);
	}

	const handleStop = () => {
		dismissModal();
		stop(current);
	};
	const reminderTime = formatReminderTime(remindAt, now);

	return (
		<>
			<span className="inline-flex items-center gap-xs rounded-sm bg-white px-s py-[6px]">
				<span className="box-border inline-flex h-[30px] w-[30px] shrink-0 animate-reminderGlow items-center justify-center rounded-full bg-character-danger">
					<BellFilled className="!flex origin-top animate-bellRing text-sm !text-white" />
				</span>
				<span className="bodyregular whitespace-nowrap text-character-primary">{`Reminder ${reminderTime}`}</span>
				{canSnooze ? (
					<Dropdown overlay={snoozeMenu} trigger={['click']} disabled={isPersisting}>
						<Button
							icon={<SnoozeIcon />}
							disabled={isPersisting}
							title="Snooze"
							aria-label="Snooze"
						/>
					</Dropdown>
				) : null}
				<OWButton
					onClick={handleStop}
					loading={isPersisting}
					title="Stop"
					aria-label="Stop"
					// A flex row like antd's own icons: as a bare inline-block the square sat ~1px low.
					icon={
						<span className="!flex justify-center">
							<span className="h-[10px] w-[10px] bg-grey-10" />
						</span>
					}
				/>
			</span>

			<Modal
				visible={dismissedReminderKey !== reminderKey}
				onCancel={dismissModal}
				footer={null}
				width={400}
				centered
			>
				<div className="flex flex-col items-center gap-xs pt-s">
					<span className="bodyregular text-character-secondary">
						Reminder{note ? ': ' : ''}
						{note ? <i>{note}</i> : null}
					</span>
					<span className="h3regular inline-flex items-center gap-s text-character-primary">
						<BellFilled className="origin-top animate-bellRing text-[28px] !text-character-danger" />
						{reminderTime}
					</span>
					<div className="flex gap-m pt-s">
						{canSnooze ? (
							<Dropdown overlay={snoozeMenu} trigger={['click']} disabled={isPersisting}>
								<Button className="min-w-[110px]">
									SNOOZE <DownOutlined />
								</Button>
							</Dropdown>
						) : null}
						<OWButton
							type="primary"
							className="min-w-[110px]"
							onClick={handleStop}
							loading={isPersisting}
						>
							STOP
						</OWButton>
					</div>
				</div>
			</Modal>

			{customSnoozeModal}
		</>
	);
};

export default connectStarActions(WorkOrderDetailReminder as any) as React.ComponentType<OwnProps>;
